import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Table,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Empty,
  Alert,
  message,
} from 'antd';
import { ExperimentOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  BiopsyProcedureType,
  type Biopsy,
  type CreateBiopsyDto,
} from '../../../../api/dermatoApi';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';
import { useDocumentLanguage } from '../../../../hooks/useDocumentLanguage';
import {
  DOCUMENT_FALLBACK_MESSAGE,
  downloadMedicalDocument,
} from '../../medicalDocuments';

const { Text } = Typography;

const PROCEDURE_OPTIONS = Object.values(BiopsyProcedureType).map((v) => ({
  value: v,
  label:
    v === 'EXCISION' ? 'Excizională'
    : v === 'INCISION' ? 'Incizională'
    : v === 'PUNCH' ? 'Punch'
    : v === 'SHAVE' ? 'Shave'
    : v === 'CORE_NEEDLE' ? 'Ac gros (core needle)'
    : v === 'FINE_NEEDLE_ASPIRATION' ? 'Aspirație ac fin'
    : 'Altă',
}));

const Step9Biopsy = () => {
  const { form, lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { user } = useAuthStore();
  const { language } = useDocumentLanguage();
  const [biopsies, setBiopsies] = useState<Biopsy[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Biopsy | null>(null);
  const [formAntd] = Form.useForm<CreateBiopsyDto>();
  const [saving, setSaving] = useState(false);
  const [downloadingReferral, setDownloadingReferral] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  const refresh = async () => {
    if (!lesion?.id) return;
    setLoadingList(true);
    try {
      const list = await dermatoApi.biopsy.getByLesion(lesion.id);
      setBiopsies(list);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesion?.id]);

  const openCreate = () => {
    setEditing(null);
    formAntd.resetFields();
    formAntd.setFieldsValue({
      biopsyDate: dayjs(),
      procedureType: BiopsyProcedureType.EXCISION,
      performedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
      sampleType: 'Tesut cutanat',
      laboratory: 'Anapat Timișoara',
    } as any);
    setModalOpen(true);
  };

  const openEdit = (b: Biopsy) => {
    setEditing(b);
    formAntd.setFieldsValue({
      ...b,
      biopsyDate: dayjs(b.biopsyDate),
      sentDate: b.sentDate ? dayjs(b.sentDate) : undefined,
    } as any);
    setModalOpen(true);
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!lesion?.id) return false;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateBiopsyDto = {
        ...values,
        biopsyDate: (values.biopsyDate as unknown as dayjs.Dayjs).toISOString(),
        sentDate: values.sentDate ? (values.sentDate as unknown as dayjs.Dayjs).toISOString() : undefined,
      };
      if (editing?.id) {
        await dermatoApi.biopsy.update(editing.id, dto);
      } else {
        await dermatoApi.biopsy.createForLesion(lesion.id, dto);
      }
      setModalOpen(false);
      await refresh();
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Salvarea pasului are loc în modal; în afara modalului nu există modificări nesalvate.
  const saveCurrentStep = async (): Promise<boolean> => {
    if (!modalOpen) return true;
    return handleSubmit();
  };

  useRegisterStepSave(saveCurrentStep);

  const handleDelete = async (id: string) => {
    await dermatoApi.biopsy.delete(id);
    await refresh();
  };

  /**
   * §1.2.6 — scrisoarea către chirurg se generează pentru leziunea selectată
   * și se descarcă direct (fără modal).
   */
  const handleReferralSurgery = async () => {
    if (!form?.id || !lesion?.id) return;
    try {
      setDownloadingReferral(true);
      setFallbackMessage(null);
      const result = await downloadMedicalDocument('referral-surgery', {
        formId: form.id,
        lang: language,
        lesionId: lesion.id,
      });
      if (result.meta.fallback) {
        setFallbackMessage(result.meta.message ?? DOCUMENT_FALLBACK_MESSAGE);
      }
      message.success('Scrisoarea către chirurg a fost descărcată.');
    } catch {
      message.error('Eroare la descărcarea scrisorii către chirurg.');
    } finally {
      setDownloadingReferral(false);
    }
  };

  if (loading) return <Card loading title="Biopsie" />;

  if (lesions.length === 0) {
    return (
      <Card title="Biopsie">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <ExperimentOutlined style={{ color: '#1890ff' }} />
          <span>Biopsie — per leziune (1:N)</span>
        </Space>
      }
      extra={
        lesion && (
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void handleReferralSurgery()}
              loading={downloadingReferral}
              disabled={!form?.id}
            >
              Scrisoare către chirurg
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Adaugă biopsie
            </Button>
          </Space>
        )
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {fallbackMessage && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Limba documentului a fost ajustată"
          description={fallbackMessage}
          data-testid="referral-fallback-alert"
        />
      )}

      {!lesion && <Alert type="warning" showIcon message="Selectează o leziune pentru a vedea/adăuga biopsii." />}

      {lesion && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text strong>{lesion.location}</Text>
                <Text type="secondary">({lesions.length === 0 ? 0 : biopsies.length} biopsii)</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          {loadingList ? (
            <Alert type="info" message="Se încarcă biopsiile…" />
          ) : biopsies.length === 0 ? (
            <Empty description="Nicio biopsie înregistrată." />
          ) : (
            <Table
              scroll={{ x: 'max-content' }}
              rowKey="id"
              dataSource={biopsies}
              size="small"
              pagination={false}
              columns={[
                { title: 'Data', dataIndex: 'biopsyDate', render: (v: string) => new Date(v).toLocaleDateString('ro-RO') },
                { title: 'Tip procedură', dataIndex: 'procedureType', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Localizare', dataIndex: 'location' },
                { title: 'Probe code', dataIndex: 'sampleCode' },
                { title: 'Laborator', dataIndex: 'laboratory' },
                { title: 'Operator', dataIndex: 'performedBy' },
                {
                  title: '',
                  width: 100,
                  render: (_: unknown, b: Biopsy) => (
                    <Space size="small">
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(b)} />
                      <Popconfirm title="Șterge biopsie?" onConfirm={() => void handleDelete(b.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </>
      )}

      <Modal
        title={editing ? `Editare biopsie ${editing.sampleCode}` : 'Adaugă biopsie'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={saving}
        okText={editing ? 'Salvează' : 'Creează'}
        cancelText="Anulează"
        destroyOnClose
      >
        <Form form={formAntd} layout="vertical" preserve={false}>
          <Form.Item name="biopsyDate" label="Data biopsiei" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="procedureType" label="Tip procedură" rules={[{ required: true }]}>
            <Select options={PROCEDURE_OPTIONS} />
          </Form.Item>
          <Form.Item name="location" label="Localizare" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="performedBy" label="Operator" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sampleType" label="Tip probă" rules={[{ required: true }]}>
            <Input placeholder="ex: Tesut cutanat, fragment" />
          </Form.Item>
          <Form.Item
            name="sampleCode"
            label="Cod probă (unic global)"
            rules={[{ required: true }]}
            extra="Trebuie să fie unic în tot sistemul"
          >
            <Input placeholder="ex: HP-2026-0042" />
          </Form.Item>
          <Form.Item name="laboratory" label="Laborator" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sentDate" label="Data trimiterii">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sentTo" label="Trimis la">
            <Input placeholder="ex: Lab central" />
          </Form.Item>
          <Form.Item name="observations" label="Observații">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Step9Biopsy;
